/**
 * Client portal Bio: the client's per-platform profile copy, platform tabs on
 * top. Edit opens the shared drawer (a bottom sheet on phones, DD-051) for the
 * active platform, without the IG handle and FB page name, which stay
 * agency-owned. Brief-share and creator sessions read only (`canEdit`).
 */

import React, { useState } from "react";
import { useOutletContext } from "react-router-dom";
import { SocialPlatform, SocialProfile } from "@/types";
import { useLocale } from "@/hooks/useLocale";
import { EditIcon, SpinnerIcon } from "@/components/icons";
import { PanelHeaderAction } from "@/components/ui/PanelHeaderAction";
import { SocialPlatformTabs } from "@/domains/Client/components/SocialPlatformTabs";
import { SocialProfileRows } from "@/domains/Client/components/SocialProfileRows";
import { SocialProfileDrawer } from "@/domains/Client/components/SocialProfileDrawer";
import { PLATFORM_LABELS, PROFILE_COPY, clientFieldsFor } from "@/domains/Client/socialProfiles";
import {
  usePortalSocialProfiles,
  useUpdatePortalSocialProfile,
} from "../hooks/usePortalSocialProfiles";

const NO_PROFILE: SocialProfile = {};

export const ClientBioRoute: React.FC = () => {
  const { clientId } = useOutletContext<{ clientId: string }>();
  const { t, locale } = useLocale();
  const [platform, setPlatform] = useState<SocialPlatform>("instagram");
  const [editing, setEditing] = useState(false);
  const { data, isLoading } = usePortalSocialProfiles(clientId);
  const update = useUpdatePortalSocialProfile(clientId);

  const profile = data?.socialProfiles[platform] ?? NO_PROFILE;
  const canEdit = data?.canEdit ?? false;

  const handleSave = async (draft: SocialProfile) => {
    try {
      await update.mutateAsync({ platform, profile: draft });
    } catch {
      throw new Error(PROFILE_COPY[locale].saveFailed);
    }
    setEditing(false);
  };

  return (
    <div className="flex-1 min-h-0 overflow-y-auto">
      <div className="w-full max-w-2xl mx-auto px-4 lg:px-6 py-4 space-y-4">
        <SocialPlatformTabs platform={platform} onChange={setPlatform} />

        {isLoading ? (
          <div className="flex justify-center py-12">
            <SpinnerIcon className="w-6 h-6 text-gray-400" />
          </div>
        ) : !data ? (
          <p className="p-6 text-center text-gray-500">{t("client.bio.loadError")}</p>
        ) : (
          <section className="bg-white rounded-2xl p-4">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-sm font-semibold text-gray-900">{PLATFORM_LABELS[platform]}</h2>
              {canEdit && (
                <PanelHeaderAction
                  label={t("client.bio.edit")}
                  icon={<EditIcon className="w-3.5 h-3.5" />}
                  onClick={() => setEditing(true)}
                />
              )}
            </div>
            <SocialProfileRows platform={platform} profile={profile} locale={locale} stacked />
          </section>
        )}
      </div>

      {canEdit && (
        <SocialProfileDrawer
          isOpen={editing}
          platform={platform}
          profile={profile}
          fields={clientFieldsFor(platform)}
          locale={locale}
          onSave={handleSave}
          onClose={() => setEditing(false)}
        />
      )}
    </div>
  );
};
